import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { Sun, Moon, LogIn } from 'lucide-react';
import { useTheme } from '@/lib/theme';

export function LoginPage() {
  const { login, user, loading } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si ya hay sesión, redirige al panel correspondiente
  useEffect(() => {
    if (!loading && user) {
      nav(user.role === 'ADMIN' ? '/admin' : '/app', { replace: true });
    }
  }, [loading, user, nav]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast(`Bienvenido, ${u.email}`);
      nav(u.role === 'ADMIN' ? '/admin' : '/app', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Credenciales inválidas');
      } else {
        setError(err instanceof Error ? err.message : 'Error de conexión');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-bg">
      <button
        type="button"
        onClick={toggle}
        aria-label="Cambiar tema"
        className="fixed top-4 right-4 p-2.5 rounded-lg text-text-dim hover:bg-card"
      >
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div className="card w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center font-extrabold text-white text-2xl mb-3 shadow-soft">
            D
          </div>
          <h1 className="text-2xl font-extrabold">diegoNutri</h1>
          <p className="text-sm text-text-dim mt-1">Inicia sesión para continuar</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
            />
          </div>
          <div>
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="text-sm text-danger bg-danger-dim px-3 py-2 rounded">{error}</div>
          )}

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            <LogIn size={18} />
            {submitting ? 'Entrando…' : 'Entrar'}
          </Button>
        </form>
      </div>
    </div>
  );
}
